/**
 * Comprehensive Field Mapping for Fecho Sub-section Document Modals
 * 
 * This file contains mappings between Lead fields and all document modal fields
 * to ensure consistent autofill across all modals.
 */

import { Lead } from '../services/api';

/**
 * Partial Lead type for components that may only have limited lead data
 */
export type PartialLead = Partial<Lead> & {
  id: number;
  name: string;
};

/**
 * Lead field groups - what data we have from the lead
 */
export interface LeadFieldGroups {
  // Name fields
  name: string;
  company?: string;
  
  // Identification
  nif: string; // dif field
  id: number;
  
  // Contact
  phone: string;
  email: string;
  
  // Address
  address: string;
  city: string;
  postalCode: string;
  postalCodePart1: string; // First 4 digits
  postalCodePart2: string; // Last 3 digits
  country: string;
}

/**
 * Field mapping groups - fields that represent the same thing across all modals
 */
export const FIELD_MAPPING_GROUPS = {
  // NAME GROUP - All variations of name fields
  NAME: [
    'Nome',
    'Nome Completo',
    'Nome Completo Autorização',
    'Nome do Cliente',
    'Novo Titular',
    'Nome1#0',
    'Nome2#0',
    'Nome1#1',
    'Nome2#1',
    'nome',
    'nomeEmpresa',
    'nomeRepresentante',
    'admin_conta_nome',
    'nome_titular_conta_bancaria',
    'telefone_movel1_nome_utilizador',
    'telefone_movel2_nome_utilizador',
    'telefone_movel3_nome_utilizador',
  ],
  
  // NIF/TAX ID GROUP
  // Note: Num_Doc_Identificação_1 and Num_Doc_Identificação_1#1 are NOT NIF fields
  // They are document identification numbers (CC/BI/Passport), not tax IDs
  NIF: [
    'NIF',
    'Contribuinte',
    'NIF Novo Titular',
    'NIF_1',
    'NIF_2',
    'nif',
    'nifEmpresa',
    'contribuinte',
    'admin_conta_nif',
    'telefone_movel1_contribuinte',
    'telefone_movel2_contribuinte',
    'telefone_movel3_contribuinte',
  ],
  
  // PHONE GROUP
  // NOTE: telefone_movel1_numero, telefone_movel2_numero, telefone_movel3_numero are EXCLUDED
  // These are specific mobile phone fields that should NOT be autofilled
  PHONE: [
    'Telefone Contacto',
    'Telemóvel de Contacto',
    'Contacto',
    'Telefone',
    'Telemóvel',
    'phone',
    'telefone_contacto',
    'contactoRepresentante',
    'admin_conta_telefone',
    'admin_conta_telemovel',
    'telemovel_aviso_faturacao',
    'telemovel',
    // EXCLUDED: 'telefone_movel1_numero', 'telefone_movel2_numero', 'telefone_movel3_numero'
  ],
  
  // EMAIL GROUP
  EMAIL: [
    'Email',
    'email',
    'emailRepresentante',
    'emailEmpresa',
    'admin_conta_email',
  ],
  
  // ADDRESS GROUP
  ADDRESS: [
    'Morada',
    'Morada Completa',
    'Endereço',
    'address',
    'moradaInstalacao',
    'morada_instalacao',
    'moradaFornecimento',
    'moradaCorrespondencia',
  ],
  
  // CITY/LOCALITY GROUP
  CITY: [
    'Cidade',
    'Localidade',
    'city',
    'localidade_instalacao',
    'regiao',
  ],
  
  // POSTAL CODE GROUP
  POSTAL_CODE: [
    'Código Postal',
    'Código Postal 4',
    'Código Postal 3',
    'cod_postal_instalacao',
    'postal_code',
  ],
  
  // IDENTIFICATION DOCUMENT GROUP
  ID_DOCUMENT: [
    'cc_bi_passaporte',
    'telefone_movel1_cc_bi_passaporte',
    'telefone_movel2_cc_bi_passaporte',
    'telefone_movel3_cc_bi_passaporte',
    'admin_conta_bi',
  ],
  
  // LEAD ID GROUP
  LEAD_ID: [
    'VRef cliente N',
    'lead_id',
    'id',
  ],
};

/**
 * Label-based matching patterns - keywords that indicate what field type it is
 * Made more restrictive to avoid false matches
 */
export const LABEL_PATTERNS = {
  NAME: [
    '^nome\\s+(completo|do cliente|do titular|do utilizador|do representante)$',
    '^nome$',
    '^primeiro nome',
    '^último nome',
    '^nome do titular',
    '^nome do utilizador',
    '^nome do representante',
    '^nome da empresa',
    '^nome completo'
  ],
  NIF: ['^nif', '^contribuinte', '^doc.*fiscal', '^identificação fiscal'],
  // PHONE pattern - but exclude numbered telemóvel fields (Telemóvel 1, Telemóvel 2, etc.)
  // These are matched separately to exclude them from autofill
  PHONE: ['^telefone', '^contacto', '^(?!telemóvel\\s*[1-4]|telemóvel\\s*[1-4]\\s)telemóvel', '^phone'],
  EMAIL: ['^email$', '^e-mail$', '^correio'],
  ADDRESS: ['^morada', '^endereço', '^address'],
  CITY: ['^cidade$', '^localidade$', '^city$'],
  POSTAL_CODE: ['^código postal', '^codigo postal', '^postal code'],
};

/**
 * Extract lead field groups from a Lead or PartialLead object
 */
export function extractLeadFields(lead: Lead | PartialLead | null): LeadFieldGroups | null {
  if (!lead) return null;
  
  const postalCodeParts = lead.postal_code ? lead.postal_code.split('-') : ['', ''];
  
  return {
    name: lead.name || '',
    company: lead.company || '',
    nif: lead.dif || '',
    id: lead.id || 0,
    phone: lead.phone || '',
    email: lead.email || '',
    address: lead.address || '',
    city: lead.city || '',
    postalCode: lead.postal_code || '',
    postalCodePart1: postalCodeParts[0] || '',
    postalCodePart2: postalCodeParts[1] || '',
    country: lead.country || 'Portugal',
  };
}

/**
 * Check if a field name matches a group
 * Uses exact matching only to avoid false positives
 */
export function fieldMatchesGroup(fieldName: string, group: string[]): boolean {
  const normalizedField = fieldName.toLowerCase().trim();
  return group.some(groupField => {
    const normalizedGroupField = groupField.toLowerCase();
    // Exact match only - no partial matching to avoid false positives
    return normalizedField === normalizedGroupField;
  });
}

/**
 * Check if a label matches a pattern group
 * Uses strict regex matching with word boundaries to avoid false matches
 */
export function labelMatchesPattern(label: string, patterns: string[]): boolean {
  if (!label) return false;
  const normalizedLabel = label.toLowerCase().trim();
  return patterns.some(pattern => {
    try {
      const regex = new RegExp(pattern, 'i');
      return regex.test(normalizedLabel);
    } catch (e) {
      // If regex is invalid, fall back to simple string matching
      return normalizedLabel.includes(pattern.toLowerCase());
    }
  });
}

/**
 * Get the appropriate value for a field based on field name and label
 */
export function getFieldValue(
  fieldName: string,
  label: string | undefined,
  leadFields: LeadFieldGroups
): string | number | undefined {
  // EXCLUDE: Telemóvel 1-4 fields should NOT be autofilled
  const fieldNameLower = fieldName.toLowerCase();
  const labelLower = label?.toLowerCase() || '';
  
  // Check for field names like "Telemóvel 1", "Telemóvel 2", etc.
  if (fieldNameLower.match(/^telemóvel\s*[1-4]$/i) || fieldNameLower.match(/^telemovel\s*[1-4]$/i)) {
    return undefined; // Don't autofill numbered telemóvel fields
  }
  
  // Check for field names with underscores like "telefone_movel1_numero"
  if (fieldNameLower.includes('telefone_movel') && 
      (fieldNameLower.includes('1_numero') || fieldNameLower.includes('2_numero') || 
       fieldNameLower.includes('3_numero') || fieldNameLower.includes('4_numero'))) {
    return undefined; // Don't autofill these fields
  }
  
  // Check labels for "Telemóvel 1", "Telemóvel 2", etc.
  if (labelLower.match(/^telemóvel\s*[1-4]$/i) || labelLower.match(/^telemovel\s*[1-4]$/i)) {
    return undefined; // Don't autofill numbered telemóvel fields
  }
  
  // Try direct field name matching first
  if (fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.NAME)) {
    return leadFields.name || leadFields.company || '';
  }
  if (fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.NIF)) {
    return leadFields.nif;
  }
  if (fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.PHONE)) {
    return leadFields.phone;
  }
  if (fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.EMAIL)) {
    return leadFields.email;
  }
  if (fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.ADDRESS)) {
    return leadFields.address;
  }
  if (fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.CITY)) {
    return leadFields.city;
  }
  if (fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.POSTAL_CODE)) {
    // Handle postal code variations
    if (fieldName.includes('4') || fieldName.toLowerCase().includes('primeira')) {
      return leadFields.postalCodePart1;
    }
    if (fieldName.includes('3') || fieldName.toLowerCase().includes('segunda')) {
      return leadFields.postalCodePart2;
    }
    return leadFields.postalCode;
  }
  if (fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.LEAD_ID)) {
    return leadFields.id.toString();
  }
  
  // Try label-based matching (only if field name didn't match)
  // This is a fallback for fields that don't have exact name matches
  if (label && !fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.NAME)) {
    if (labelMatchesPattern(label, LABEL_PATTERNS.NAME)) {
      return leadFields.name || leadFields.company || '';
    }
    if (!fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.NIF) && labelMatchesPattern(label, LABEL_PATTERNS.NIF)) {
      return leadFields.nif;
    }
    // EXCLUDE: Don't autofill numbered telemóvel fields (Telemóvel 1, 2, 3, 4)
    // Check both field name and label to exclude these
    const isNumberedTelemovel = 
      fieldNameLower.match(/^telemóvel\s*[1-4]$/i) || 
      fieldNameLower.match(/^telemovel\s*[1-4]$/i) ||
      labelLower.match(/^telemóvel\s*[1-4]$/i) || 
      labelLower.match(/^telemovel\s*[1-4]$/i);
    
    if (!isNumberedTelemovel) {
      if (!fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.PHONE) && labelMatchesPattern(label, LABEL_PATTERNS.PHONE)) {
        return leadFields.phone;
      }
    }
    if (!fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.EMAIL) && labelMatchesPattern(label, LABEL_PATTERNS.EMAIL)) {
      return leadFields.email;
    }
    if (!fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.ADDRESS) && labelMatchesPattern(label, LABEL_PATTERNS.ADDRESS)) {
      return leadFields.address;
    }
    if (!fieldMatchesGroup(fieldName, FIELD_MAPPING_GROUPS.CITY) && labelMatchesPattern(label, LABEL_PATTERNS.CITY)) {
      return leadFields.city;
    }
    if (labelMatchesPattern(label, LABEL_PATTERNS.POSTAL_CODE)) {
      if (label.toLowerCase().includes('4') || label.toLowerCase().includes('primeira')) {
        return leadFields.postalCodePart1;
      }
      if (label.toLowerCase().includes('3') || label.toLowerCase().includes('segunda')) {
        return leadFields.postalCodePart2;
      }
      return leadFields.postalCode;
    }
  }
  
  return undefined;
}

/**
 * Create comprehensive autofill object for all modals
 */
export function createAutofillData(lead: Lead | PartialLead | null): Record<string, any> {
  if (!lead) return {};
  
  const leadFields = extractLeadFields(lead);
  if (!leadFields) return {};
  
  const postalCodeParts = lead.postal_code ? lead.postal_code.split('-') : ['', ''];
  const today = new Date();
  
  // Base autofill data
  const autofill: Record<string, any> = {
    // Name variations
    'Nome': leadFields.name,
    'Nome Completo': leadFields.name,
    'Nome Completo Autorização': leadFields.name,
    'Nome do Cliente': leadFields.name,
    'Novo Titular': leadFields.name,
    'nome': leadFields.name,
    'nomeEmpresa': leadFields.company || leadFields.name,
    'nomeRepresentante': leadFields.name,
    'admin_conta_nome': leadFields.name,
    'nome_titular_conta_bancaria': leadFields.name,
    
    // NIF variations
    'NIF': leadFields.nif,
    'Contribuinte': leadFields.nif,
    'NIF Novo Titular': leadFields.nif,
    'Num_Doc_Identificação_1': leadFields.nif,
    'Num_Doc_Identificação_1#1': leadFields.nif,
    'NIF_1': leadFields.nif,
    'NIF_2': leadFields.nif,
    'nif': leadFields.nif,
    'nifEmpresa': leadFields.nif,
    'contribuinte': leadFields.nif,
    'admin_conta_nif': leadFields.nif,
    
    // Phone variations
    'Telefone Contacto': leadFields.phone,
    'Telemóvel de Contacto': leadFields.phone,
    'Contacto': leadFields.phone,
    'Telefone': leadFields.phone,
    'Telemóvel': leadFields.phone,
    'telefone_contacto': leadFields.phone,
    'contactoRepresentante': leadFields.phone,
    'admin_conta_telefone': leadFields.phone,
    'admin_conta_telemovel': leadFields.phone,
    'telemovel_aviso_faturacao': leadFields.phone,
    'telemovel': leadFields.phone,
    
    // Email variations
    'Email': leadFields.email,
    'email': leadFields.email,
    'emailRepresentante': leadFields.email,
    'emailEmpresa': leadFields.email,
    'admin_conta_email': leadFields.email,
    
    // Address variations
    'Morada': leadFields.address,
    'Morada Completa': leadFields.address,
    'Endereço': leadFields.address,
    'moradaInstalacao': leadFields.address,
    'morada_instalacao': leadFields.address,
    'moradaFornecimento': leadFields.address,
    'moradaCorrespondencia': leadFields.address,
    
    // City variations
    'Cidade': leadFields.city,
    'Localidade': leadFields.city,
    'localidade_instalacao': leadFields.city,
    'regiao': leadFields.city,
    
    // Postal code variations
    'Código Postal': leadFields.postalCode,
    'Código Postal 4': leadFields.postalCodePart1,
    'Código Postal 3': leadFields.postalCodePart2,
    'cod_postal_instalacao': leadFields.postalCode,
    
    // Lead ID
    'VRef cliente N': leadFields.id.toString(),
    'lead_id': leadFields.id,
    
    // Date fields (current date)
    'Dia': today.getDate().toString(),
    'Mês': (today.getMonth() + 1).toString(),
    'Ano': today.getFullYear().toString(),
    'dia': today.getDate().toString(),
    'mes': (today.getMonth() + 1).toString(),
    'ano': today.getFullYear().toString(),
    'Hora': today.getHours().toString(),
    'Minuto': today.getMinutes().toString(),
    'hora': today.getHours().toString(),
    'minuto': today.getMinutes().toString(),
  };
  
  // Special handling for split name fields (NOS documents)
  if (leadFields.name) {
    const fullName = leadFields.name;
    autofill['Nome1#0'] = fullName.slice(0, 16);
    autofill['Nome2#0'] = fullName.slice(16);
    autofill['Nome1#1'] = fullName.slice(0, 16);
    autofill['Nome2#1'] = fullName.slice(16);
  }
  
  return autofill;
}

/** True if the value should block overwriting with inferred autofill (empty string = not meaningful). */
export function hasMeaningfulValue(v: any): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'boolean') return true;
  if (typeof v === 'number') return !Number.isNaN(v);
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

export type ApplyAutofillOptions = {
  /** Saved / in-session PDF field bag — same field names across templates and prior runs */
  preloadedValues?: Record<string, any>;
};

/**
 * Apply autofill to field values using comprehensive matching
 */
export function applyAutofill(
  fieldName: string,
  fieldLabel: string | undefined,
  relatedField: string | undefined,
  currentValue: any,
  leadFields: LeadFieldGroups,
  options?: ApplyAutofillOptions
): any {
  const pre = options?.preloadedValues;

  const pickPreloaded = (key: string | undefined): any => {
    if (!key || !pre) return undefined;
    const pv = pre[key];
    return hasMeaningfulValue(pv) ? pv : undefined;
  };

  // EXCLUDE: Telemóvel 1-4 fields should NOT be autofilled
  const fieldNameLower = fieldName.toLowerCase();
  const labelLower = fieldLabel?.toLowerCase() || '';

  // Check for field names like "Telemóvel 1", "Telemóvel 2", etc.
  if (fieldNameLower.match(/^telemóvel\s*[1-4]$/i) || fieldNameLower.match(/^telemovel\s*[1-4]$/i)) {
    return currentValue;
  }

  // Check for field names with underscores like "telefone_movel1_numero"
  if (
    fieldNameLower.includes('telefone_movel') &&
    (fieldNameLower.includes('1_numero') ||
      fieldNameLower.includes('2_numero') ||
      fieldNameLower.includes('3_numero') ||
      fieldNameLower.includes('4_numero'))
  ) {
    return currentValue;
  }

  // Check labels for "Telemóvel 1", "Telemóvel 2", etc.
  if (labelLower.match(/^telemóvel\s*[1-4]$/i) || labelLower.match(/^telemovel\s*[1-4]$/i)) {
    return currentValue;
  }

  let effective = currentValue;
  if (!hasMeaningfulValue(effective)) effective = pickPreloaded(fieldName);
  if (!hasMeaningfulValue(effective) && relatedField) effective = pickPreloaded(relatedField);
  if (hasMeaningfulValue(effective)) return effective;

  // Try related_field from lead mapping
  if (relatedField) {
    const relatedValue = getFieldValue(relatedField, undefined, leadFields);
    if (relatedValue !== undefined && `${relatedValue}`.trim() !== '') {
      return relatedValue;
    }
  }

  // Try direct field name matching
  const directValue = getFieldValue(fieldName, undefined, leadFields);
  if (directValue !== undefined && `${directValue}`.trim() !== '') {
    return directValue;
  }

  // Try label-based matching
  if (fieldLabel) {
    const labelValue = getFieldValue('', fieldLabel, leadFields);
    if (labelValue !== undefined && `${labelValue}`.trim() !== '') {
      return labelValue;
    }
  }

  return currentValue;
}

export type TemplateFieldLike = { name: string; label?: string; related_field?: string };

/**
 * Merge saved + in-session PDF field values with lead autofill for template fields only.
 * Priority per field: existing base value → related_field in base → applyAutofill (lead) → createAutofillData direct key.
 */
export function mergeTemplateFieldValues(
  templateFields: TemplateFieldLike[],
  lead: Lead | PartialLead | null,
  base: Record<string, any>
): Record<string, any> {
  if (!templateFields.length) return { ...base };
  const leadFields = extractLeadFields(lead);
  const autofillMap = createAutofillData(lead);
  const out: Record<string, any> = { ...base };

  for (const field of templateFields) {
    const name = field.name;
    let v: any = out[name];
    if (!hasMeaningfulValue(v) && field.related_field) {
      v = out[field.related_field];
    }

    if (leadFields) {
      v = applyAutofill(name, field.label, field.related_field, v, leadFields, { preloadedValues: out });
    }

    if (!hasMeaningfulValue(v) && autofillMap[name] !== undefined && autofillMap[name] !== null) {
      const av = autofillMap[name];
      if (hasMeaningfulValue(av)) v = av;
    }

    if (hasMeaningfulValue(v)) {
      out[name] = v;
    }
  }

  // Second pass: related PDF field already filled in `out` but target still empty
  for (const field of templateFields) {
    if (!field.related_field) continue;
    if (hasMeaningfulValue(out[field.name])) continue;
    const src = out[field.related_field];
    if (hasMeaningfulValue(src)) {
      out[field.name] = src;
    }
  }

  return out;
}

